import React, { useRef } from 'react';
import { Download, Printer } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { useLanguage } from '../context/LanguageContext';

const MukhyaMantriAbhiyanResolutionForm = () => {
  const { language } = useLanguage();
  const componentRef = useRef();

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = () => {
    const element = componentRef.current;
    const opt = {
      margin: 0,
      filename: language === 'marathi' ? 'Mukhyamantri_Abhiyan_Resolution.pdf' : 'Mukhyamantri_Abhiyan_Resolution_EN.pdf',
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 3, useCORS: true, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().from(element).set(opt).save();
  };

  const SmallTable = ({ subjectNo, subjectTitle }) => (
    <div className="mb-8 w-full px-8">
      <div className="flex items-stretch mb-4 w-full">
        <div className="bg-[#c0504d] text-white font-bold rounded-md px-2 py-2 flex items-center justify-center shadow-sm border border-[#c0504d] w-28 flex-shrink-0 text-[15px]">
           {language === 'marathi' ? 'विषय:' : 'Subject:'} {subjectNo}
        </div>
        <div className="bg-white text-black font-bold rounded-md px-4 py-2 flex items-center justify-center shadow-sm border-2 border-[#c0504d] ml-4 flex-grow text-[14px]">
           {subjectTitle}
        </div>
      </div>
      <table className="w-full border-collapse border border-black text-[15px]">
        <thead>
           <tr>
              <th className="border border-black p-1 w-12 text-left font-normal pl-2">{language === 'marathi' ? 'अ.क्र.' : 'Sr.No.'}</th>
              <th className="border border-black p-1 text-left font-normal pl-2">{language === 'marathi' ? 'नाव' : 'Name'}</th>
              <th className="border border-black p-1 w-48 text-left font-normal pl-2">{language === 'marathi' ? 'पद' : 'Designation'}</th>
           </tr>
        </thead>
        <tbody>
           <tr>
              <td className="border border-black h-7"></td>
              <td className="border border-black h-7"></td>
              <td className="border border-black h-7 px-2">{language === 'marathi' ? 'गटप्रमुख' : 'Group Head'}</td>
           </tr>
           <tr>
              <td className="border border-black h-7"></td>
              <td className="border border-black h-7"></td>
              <td className="border border-black h-7 px-2">{language === 'marathi' ? 'सदस्य' : 'Member'}</td>
           </tr>
           <tr>
              <td className="border border-black h-7"></td>
              <td className="border border-black h-7"></td>
              <td className="border border-black h-7 px-2">{language === 'marathi' ? 'सदस्य' : 'Member'}</td>
           </tr>
           <tr>
              <td className="border border-black h-7"></td>
              <td className="border border-black h-7"></td>
              <td className="border border-black h-7 px-2">{language === 'marathi' ? 'सदस्य' : 'Member'}</td>
           </tr>
           <tr>
              <td className="border border-black h-7"></td>
              <td className="border border-black h-7"></td>
              <td className="border border-black h-7 px-2">{language === 'marathi' ? 'सदस्य' : 'Member'}</td>
           </tr>
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-200 py-8 font-sans print:py-0 print:bg-white flex flex-col items-center">
      <style>
        {`
          .page {
            width: 210mm;
            height: 297mm;
            padding: 20mm;
            margin: 0 auto 20px auto;
            box-sizing: border-box;
            position: relative;
            background-color: white;
            overflow: hidden;
            box-shadow: 0 0 15px rgba(0,0,0,0.1);
          }
          
          .dashed-line {
            border-bottom: 1px dashed black;
            display: inline-block;
          }

          @media print {
            @page {
              size: A4 portrait;
              margin: 0mm !important;
            }
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              width: 210mm !important;
              height: 297mm !important;
              background-color: white !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .page {
              margin: 0 !important;
              box-shadow: none !important;
              width: 210mm !important;
              height: 297mm !important;
              page-break-after: always !important;
              break-after: page !important;
              break-inside: avoid !important;
              border: none !important;
            }
            .no-print {
              display: none !important;
            }
            .min-h-screen {
              min-height: auto !important;
              padding: 0 !important;
            }
          }
        `}
      </style>
      
      {/* Controls Container */}
      <div className="w-full max-w-[210mm] mb-6 flex justify-end space-x-4 print:hidden px-4 no-print">
        <button
          onClick={handlePrint}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 shadow-sm transition-colors"
        >
          <Printer className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'Print' : 'Print'}
        </button>
        <button
          onClick={handleDownloadPdf}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 shadow-sm transition-colors"
        >
          <Download className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'Download PDF' : 'Download PDF'}
        </button>
      </div>

      <div ref={componentRef} className="flex flex-col items-center text-black">
        {/* PAGE 1 */}
        <div className="bg-white relative box-border flex flex-col page">
           <div className="flex justify-center items-center text-[15px] font-bold mb-2">
              <span>{language === 'marathi' ? 'कार्यालय ग्रामपंचायत' : 'Gram Panchayat Office'}</span>
              <span className="dashed-line w-40 h-4 mx-1"></span>
              <span>{language === 'marathi' ? 'ता' : 'Tal'}</span>
              <span className="dashed-line w-32 h-4 mx-1"></span>
              <span>{language === 'marathi' ? 'जि.' : 'Dist.'}</span>
              <span className="dashed-line w-32 h-4 mx-1"></span>
           </div>

           <div className="text-center text-[15px] mb-2">
              <span className="font-bold">{language === 'marathi' ? 'दिनांक:- &nbsp; &nbsp; &nbsp; &nbsp;/ &nbsp; &nbsp; &nbsp; &nbsp;/२० &nbsp; &nbsp; रोजी झालेल्या ग्रामसभा बैठकीचे इतिवृत्त नोंदवही' : 'Date:- &nbsp; &nbsp; &nbsp; &nbsp;/ &nbsp; &nbsp; &nbsp; &nbsp;/20 &nbsp; &nbsp; Minutes Register of Gramsabha Meeting Held On'}</span>
           </div>

           <table className="w-full border-collapse border border-black text-[14px]">
              <thead>
                 <tr>
                    <th className="border border-black p-1 w-12 text-center font-normal">{language === 'marathi' ? 'विषय नंबर' : 'Subject No.'}</th>
                    <th className="border border-black p-1 w-12 text-center font-normal">{language === 'marathi' ? 'ठराव नंबर' : 'Resolution No.'}</th>
                    <th className="border border-black p-2 text-center text-[15px] font-normal">{language === 'marathi' ? 'सभेपुढे विचाराकरीता आलेले विषय व ठराव' : 'Subjects and Resolutions brought before the meeting for consideration'}</th>
                    <th className="border border-black p-1 w-24 text-center font-normal leading-tight">{language === 'marathi' ? 'शेरे आणि तारखेसह ठरावाची अंमल- बजावणी' : 'Implementation of resolution with remarks and date'}</th>
                 </tr>
              </thead>
              <tbody className="h-[40px]">
                 <tr>
                    <td className="border border-black"></td>
                    <td className="border border-black"></td>
                    <td className="border border-black"></td>
                    <td className="border border-black"></td>
                 </tr>
              </tbody>
           </table>

           <div className="border border-black border-t-0 p-3 flex-grow flex flex-col relative text-[15px] leading-snug space-y-1">
              <p className="flex items-end flex-wrap indent-8">
                 <span>{language === 'marathi' ? 'आज दिनांक : &nbsp; &nbsp; &nbsp;/ &nbsp; &nbsp; &nbsp; /२० &nbsp; &nbsp; &nbsp; &nbsp; वार रोजी ग्रामपंचायत कार्यालय' : 'Today Date : &nbsp; &nbsp; &nbsp;/ &nbsp; &nbsp; &nbsp; /20 &nbsp; &nbsp; &nbsp; &nbsp; Day, at Gram Panchayat Office'}</span>
                 <span className="dashed-line flex-grow h-4 mx-1"></span>
                 <span>{language === 'marathi' ? 'ची सर्वसाधारण/विशेष' : "'s General/Special"}</span>
                 <span className="w-full mt-1 flex items-end">
                    <span>{language === 'marathi' ? 'ग्रामसभा सरपंच श्री/सौ.' : 'Gramsabha Sarpanch Mr./Mrs.'}</span>
                    <span className="dashed-line flex-grow h-4 mx-1"></span>
                    <span>{language === 'marathi' ? 'यांच्या अध्यक्षतेखाली ग्रामपंचायत कार्यालयात' : 'under the chairmanship of, in the Gram Panchayat Office'}</span>
                 </span>
                 <span className="w-full mt-1 flex items-end">
                    <span>{language === 'marathi' ? 'सकाळी/दुपारी ठीक :' : 'Exactly at Morning/Afternoon :'}</span>
                    <span className="dashed-line w-16 h-4 mx-2"></span>
                    <span>{language === 'marathi' ? 'वाजता बोलविण्यात आली.' : 'was convened.'}</span>
                 </span>
              </p>
              
              <p className="indent-8 mt-1">{language === 'marathi' ? 'या सभेत खालील ठराव मांडण्यात आला.' : 'The following resolution was moved in this meeting.'}</p>

              <div className="flex mt-1">
                 <span className="font-bold text-[16px] mr-2">{language === 'marathi' ? 'विषय:' : 'Subject:'}</span>
                 <span className="font-bold text-[16px] ml-2 text-justify">{language === 'marathi' ? 'मुख्यमंत्री समृद्ध पंचायतराज अभियान- ग्रामस्तरीय समितीची स्थापना करणे व अभियान राबविणेबाबत.' : 'Chief Minister Samruddha Panchayat Raj Abhiyan - Regarding establishment of village level committee and implementation of campaign.'}</span>
              </div>

              <p className="text-justify indent-12 mt-2">
                 {language === 'marathi' ? 'सुचक यांनी सुचविले की, ग्रामीण भागात स्थानिक स्वराज्य संस्था म्हणून प्रामुख्याने काम करणाऱ्या ग्रामपंचायतीस, कामगिरीनुसार प्रोत्साहित करुन त्यांची कार्यक्षमता वाढविण्याकरिता, उत्कृष्ट काम करणाऱ्या ग्रामपंचायत, पंचायत समिती व जिल्हा परिषदांसाठी सन २०२५-२६ या आर्थिक वर्षापासून तालुका, जिल्हा, महसूल विभाग व राज्य अशा चार स्तरांवर प्रोत्साहन देण्याकरिता “मुख्यमंत्री समृद्ध पंचायतराज अभियान” हे पुरस्कार अभियान राबविण्यास शासनाने मान्यता दिलेली आहे. सदरील अभियान आपल्या ग्रामपंचायतीमध्ये राबविण्यासाठी ग्रामस्तरीय समितीची स्थापना करावयाची आहे. तसेच आवश्यकतेनुसार अभियानाच्या विषयानुसार लहान गट तयार करायचे आहेत. त्यास अनुमोदक यांनी अनुमोदन दिले.' : 'The proposer suggested that, in order to encourage the Gram Panchayats mainly working as local self-government institutions in rural areas according to their performance and to increase their efficiency, the government has approved the implementation of the award campaign “Chief Minister Samruddha Panchayat Raj Abhiyan” from the financial year 2025-26 to provide encouragement at four levels: Taluka, District, Revenue Division, and State for outstanding Gram Panchayats, Panchayat Samitis, and Zilla Parishads. To implement this campaign in our Gram Panchayat, a village-level committee needs to be established. Also, as needed, small groups based on the subjects of the campaign should be formed. The seconder seconded this.'}
              </p>

              <p className="text-justify indent-12 mt-2">
                 {language === 'marathi' ? (
                   <>त्यानंतर ग्रामपंचायत अधिकारी यांनी मुख्यमंत्री समृद्ध पंचायतराज अभियान राबविणे बाबत शासन निर्णय- ग्राम विकास विभाग <span className="text-blue-700">शासन निर्णय क्र. मुसंपअ- २०२५/प्र.क्र. १५२, E-११२३०५७ /यो-११ दिनांक ६ ऑगस्ट २०२५</span> चे सभेत वाचन केले व अभियानाबाबत माहिती दिली.</>
                 ) : (
                   <>After that, the Gram Panchayat Officer read the Government Resolution regarding the implementation of the Chief Minister Samruddha Panchayat Raj Abhiyan - Rural Development Department <span className="text-blue-700">Government Resolution No. Musampa-2025/C.R.152, E-1123057 / Yo-11 dated August 6, 2025</span> in the meeting and gave information about the campaign.</>
                 )}
              </p>

              <p className="text-justify indent-12 mt-2">
                 {language === 'marathi' ? (
                   <>या विषयावर ग्रामसभेत चर्चा होऊन पुढिलप्रमाणे <span className="font-bold">मुख्यमंत्री समृद्ध पंचायतराज अभियान</span> – ग्रामस्तरीय समितीची निवड करण्यात आली.</>
                 ) : (
                   <>After discussion on this subject in the Gramsabha, the selection of <span className="font-bold">Chief Minister Samruddha Panchayat Raj Abhiyan</span> - Village Level Committee was made as follows.</>
                 )}
              </p>
              <p className="text-[14px]">
                 {language === 'marathi' ? '(टिप- ग्रामपंचायतीचे सर्व पदाधिकारी व गाव पातळीवरील सर्व अधिकारी/कर्मचारी सदस्य म्हणून कार्यरत राहतील.)' : '(Note- All office bearers of the Gram Panchayat and all officers/employees at the village level will act as members.)'}
              </p>

              <div className="mt-2 w-full">
                 <table className="w-full border-collapse border border-black text-[14px]">
                    <thead>
                       <tr>
                          <th className="border border-black p-1 w-10 text-left font-bold pl-2">{language === 'marathi' ? 'अ.क्र.' : 'Sr.No.'}</th>
                          <th className="border border-black p-1 text-left font-bold pl-2">{language === 'marathi' ? 'नाव' : 'Name'}</th>
                          <th className="border border-black p-1 w-56 text-left font-bold pl-2"></th>
                          <th className="border border-black p-1 w-24 text-left font-bold pl-2">{language === 'marathi' ? 'पद' : 'Designation'}</th>
                       </tr>
                    </thead>
                    <tbody>
                       <tr><td className="border border-black h-6"></td><td className="border border-black"></td><td className="border border-black px-2">{language === 'marathi' ? 'सरपंच/प्रशासक' : 'Sarpanch/Administrator'}</td><td className="border border-black font-bold px-2">{language === 'marathi' ? 'अध्यक्ष' : 'President'}</td></tr>
                       <tr><td className="border border-black h-6"></td><td className="border border-black"></td><td className="border border-black px-2">{language === 'marathi' ? 'उपसरपंच' : 'Deputy Sarpanch'}</td><td className="border border-black px-2">{language === 'marathi' ? 'सदस्य' : 'Member'}</td></tr>
                       {[...Array(10)].map((_, i) => (
                         <tr key={`p1-${i}`}>
                            <td className="border border-black h-6"></td>
                            <td className="border border-black"></td>
                            <td className="border border-black px-2">{language === 'marathi' ? 'ग्रा.पं. सदस्य/ पदाधिकारी' : 'G.P. Member / Office Bearer'}</td>
                            <td className="border border-black px-2">{language === 'marathi' ? 'सदस्य' : 'Member'}</td>
                         </tr>
                       ))}
                    </tbody>
                 </table>
              </div>
           </div>
        </div>

        {/* PAGE 2 */}
        <div className="bg-white relative box-border flex flex-col page">
           <div className="border border-black border-t-0 p-3 flex-grow flex flex-col relative text-[15px] leading-snug">
              <div className="w-full">
                 <table className="w-full border-collapse border border-black border-t-0 text-[14px]">
                    <tbody>
                       {[...Array(8)].map((_, i) => (
                         <tr key={`p2-${i}`}>
                            <td className="border border-black w-10 h-6"></td>
                            <td className="border border-black"></td>
                            <td className="border border-black px-2 w-56">{language === 'marathi' ? 'ग्रा.पं. सदस्य/ पदाधिकारी' : 'G.P. Member / Office Bearer'}</td>
                            <td className="border border-black px-2 w-24">{language === 'marathi' ? 'सदस्य' : 'Member'}</td>
                         </tr>
                       ))}
                       <tr><td className="border border-black h-6"></td><td className="border border-black"></td><td className="border border-black px-2">{language === 'marathi' ? 'आशा स्वयंसेविका' : 'ASHA Volunteer'}</td><td className="border border-black px-2">{language === 'marathi' ? 'सदस्य' : 'Member'}</td></tr>
                       <tr><td className="border border-black h-6"></td><td className="border border-black"></td><td className="border border-black px-2">{language === 'marathi' ? 'अंगणवाडी सेविका' : 'Anganwadi Worker'}</td><td className="border border-black px-2">{language === 'marathi' ? 'सदस्य' : 'Member'}</td></tr>
                       <tr><td className="border border-black h-6"></td><td className="border border-black"></td><td className="border border-black px-2">{language === 'marathi' ? 'पोलीस पाटील' : 'Police Patil'}</td><td className="border border-black px-2">{language === 'marathi' ? 'सदस्य' : 'Member'}</td></tr>
                       <tr><td className="border border-black h-6"></td><td className="border border-black"></td><td className="border border-black px-2">{language === 'marathi' ? 'मुख्याध्यापक' : 'Headmaster'}</td><td className="border border-black px-2">{language === 'marathi' ? 'सदस्य' : 'Member'}</td></tr>
                       <tr><td className="border border-black h-6"></td><td className="border border-black"></td><td className="border border-black px-2">{language === 'marathi' ? 'ग्राम महसूल अधिकारी' : 'Village Revenue Officer'}</td><td className="border border-black px-2">{language === 'marathi' ? 'सदस्य' : 'Member'}</td></tr>
                       <tr><td className="border border-black h-6"></td><td className="border border-black"></td><td className="border border-black px-2">{language === 'marathi' ? 'सहाय्यक कृषि अधिकारी (कृषि सहाय्यक)' : 'Assistant Agriculture Officer (Agriculture Assistant)'}</td><td className="border border-black px-2">{language === 'marathi' ? 'सदस्य' : 'Member'}</td></tr>
                       <tr><td className="border border-black h-6"></td><td className="border border-black"></td><td className="border border-black px-2">{language === 'marathi' ? 'वैद्यकीय अधिकारी /आरोग्य सेवक' : 'Medical Officer / Health Worker'}</td><td className="border border-black px-2">{language === 'marathi' ? 'सदस्य' : 'Member'}</td></tr>
                       <tr><td className="border border-black h-6"></td><td className="border border-black"></td><td className="border border-black px-2"></td><td className="border border-black px-2"></td></tr>
                       <tr><td className="border border-black h-6"></td><td className="border border-black"></td><td className="border border-black px-2"></td><td className="border border-black px-2"></td></tr>
                       <tr><td className="border border-black h-6"></td><td className="border border-black"></td><td className="border border-black px-2"></td><td className="border border-black px-2"></td></tr>
                       <tr><td className="border border-black h-6"></td><td className="border border-black"></td><td className="border border-black px-2">{language === 'marathi' ? 'ग्रामपंचायत अधिकारी' : 'Gram Panchayat Officer'}</td><td className="border border-black font-bold px-2">{language === 'marathi' ? 'सदस्य- सचिव' : 'Member-Secretary'}</td></tr>
                    </tbody>
                 </table>
              </div>

              <p className="text-justify indent-12 mt-4">
                 {language === 'marathi' ? 'वरीलप्रमाणे समितीची निवड बहुमताने/ सर्वानुमते करण्यात आली. तसेच आवश्यकतेनुसार विषयावर लहान गट तयार करण्यास मान्यता देण्यात आली. ग्रामपंचायत कार्यक्षेत्रात मुख्यमंत्री समृद्ध पंचायतराज अभियान राबविण्यास मान्यता देण्यात आली व ठराव बहुमताने/सर्वानुमते संमत करण्यात आला.' : 'The committee was selected by majority/unanimously as above. Also, approval was given to form small groups on the subject as needed. Approval was given to implement the Chief Minister Samruddha Panchayat Raj Abhiyan in the Gram Panchayat jurisdiction, and the resolution was passed by majority/unanimously.'}
              </p>

              <div className="flex mt-8 justify-between w-full">
                 <div className="flex w-1/2">
                    <span className="mr-1">{language === 'marathi' ? 'सुचक:' : 'Proposer:'}</span>
                    <span className="dashed-line flex-grow h-4"></span>
                 </div>
                 <div className="flex w-1/2 ml-4">
                    <span className="mr-1">{language === 'marathi' ? 'अनुमोदन:' : 'Seconder:'}</span>
                    <span className="dashed-line flex-grow h-4"></span>
                 </div>
              </div>

              <div className="flex justify-center mt-32 space-x-32 w-full pl-12">
                 <div className="flex flex-col items-center w-48">
                    <p className="font-bold mb-2">{language === 'marathi' ? 'ग्रामपंचायत अधिकारी' : 'Gram Panchayat Officer'}</p>
                 </div>
                 <div className="flex flex-col items-center w-32">
                    <p className="font-bold mb-2">{language === 'marathi' ? 'सरपंच' : 'Sarpanch'}</p>
                 </div>
              </div>
              <div className="flex justify-center w-full mt-2 pl-12">
                 <div className="flex items-end">
                    <span className="font-bold mr-2">{language === 'marathi' ? 'ग्रामपंचायत' : 'Gram Panchayat'}</span>
                    <span className="dashed-line w-[300px] h-4"></span>
                 </div>
              </div>
           </div>
        </div>

        {/* PAGE 3 */}
        <div className="bg-white relative box-border flex flex-col page pt-12 items-center">
           <h2 className="text-[20px] font-bold">{language === 'marathi' ? 'मुख्यमंत्री समृद्ध पंचायतराज अभियान' : 'Chief Minister Samruddha Panchayat Raj Abhiyan'}</h2>
           <h3 className="text-[18px] font-bold border-b-2 border-black inline-block mb-12">{language === 'marathi' ? 'विषयावर लहान गट' : 'Small groups on the subject'}</h3>

           <SmallTable subjectNo={language === 'marathi' ? '१' : '1'} subjectTitle={language === 'marathi' ? 'सुशासन युक्त पंचायत (लोकाभिमुख सक्षम पंचायत प्रशासन ) Governance) तयार करणे)' : 'Creating a Panchayat with Good Governance (People-oriented competent Panchayat Administration)'} />
           <SmallTable subjectNo={language === 'marathi' ? '२' : '2'} subjectTitle={language === 'marathi' ? 'सक्षम पंचायत (स्वनिधी, CSR व लोकवर्गणीतून पंचायत राज संस्थांना आर्थिक दृष्ट्या सक्षम करणे)' : 'Competent Panchayat (Empowering Panchayat Raj institutions financially through own funds, CSR and public contribution)'} />
           <SmallTable subjectNo={language === 'marathi' ? '३' : '3'} subjectTitle={language === 'marathi' ? 'जल समृद्ध, स्वच्छ व हरित गाव निर्माण करणे' : 'Creating a water-rich, clean, and green village'} />
        </div>

        {/* PAGE 4 */}
        <div className="bg-white relative box-border flex flex-col page pt-16 items-center">
           <SmallTable subjectNo={language === 'marathi' ? '४' : '4'} subjectTitle={language === 'marathi' ? 'मनरेगा व इतर योजनांचे अभिसरण करणे' : 'Convergence of MGNREGA and other schemes'} />
           <SmallTable subjectNo={language === 'marathi' ? '५' : '5'} subjectTitle={language === 'marathi' ? 'गावपातळीवरील संस्था सक्षमीकरण करणे' : 'Empowerment of village-level institutions'} />
           <SmallTable subjectNo={language === 'marathi' ? '६' : '6'} subjectTitle={language === 'marathi' ? 'उपजिविका विकास, सामाजिक न्याय' : 'Livelihood development, Social justice'} />
        </div>

        {/* PAGE 5 */}
        <div className="bg-white relative box-border flex flex-col page pt-16 items-center">
           <SmallTable subjectNo={language === 'marathi' ? '७' : '7'} subjectTitle={language === 'marathi' ? 'लोकसहभाग व श्रमदान माध्यमातून लोकचळवळ निर्माण करणे' : 'Creating a mass movement through public participation and Shramdaan'} />
        </div>

      </div>
    </div>
  );
};

export default MukhyaMantriAbhiyanResolutionForm;
